import type { Button } from '@/shared/components/ui/button'

import React from 'react'
import { Link } from '@tanstack/react-router'
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	ChevronsUpDownIcon,
	HeadsetIcon,
	LogOutIcon,
	SettingsIcon,
} from 'lucide-react'
import { useTheme } from 'next-themes'
import { useTranslation } from 'react-i18next'

import { useSession } from '@/entities/session'

import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar'
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
	SidebarFooter,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from '@/shared/components/ui/sidebar'
import { ampli } from '@/shared/lib/ampli'
import { getInitials } from '@/shared/lib/avatar-helper'
import { useModals } from '@/shared/lib/modal-manager'

export default function AppSidebarFooter() {
	const { user } = useSession()
	const username = [user?.first_name, user?.last_name].join(' ')

	return (
		<SidebarFooter>
			<SidebarMenu>
				<SidebarMenuItem>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<SidebarMenuButton
								size='lg'
								className='cursor-pointer data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
							>
								<Avatar className='border'>
									<AvatarFallback>{getInitials(username)}</AvatarFallback>
								</Avatar>
								<div className='grid flex-1 text-left text-sm leading-tight'>
									<span className='truncate font-medium'>{username}</span>
									<span className='truncate text-xs text-muted-foreground'>
										{user!.email}
									</span>
								</div>
								<ChevronsUpDownIcon className='ml-auto size-4' />
							</SidebarMenuButton>
						</DropdownMenuTrigger>
						<AppSidebarFooterDropdown />
					</DropdownMenu>
				</SidebarMenuItem>
				<SidebarTrigger />
			</SidebarMenu>
		</SidebarFooter>
	)
}

export function AppSidebarFooterDropdown() {
	const { t } = useTranslation()
	const { theme, setTheme } = useTheme()
	const { showModal } = useModals()
	const { user, logout } = useSession()
	const username = [user?.first_name, user?.last_name].join(' ')

	return (
		<DropdownMenuContent
			className='w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg'
			side='top'
			align='start'
			sideOffset={4}
		>
			<DropdownMenuLabel className='p-0 font-normal'>
				<div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
					<Avatar className='border'>
						<AvatarFallback>{getInitials(username)}</AvatarFallback>
					</Avatar>
					<div className='grid flex-1 text-left text-sm leading-tight'>
						<span className='truncate font-medium'>{username}</span>
						<span className='truncate text-xs'>{user!.email || ''}</span>
					</div>
				</div>
			</DropdownMenuLabel>
			<DropdownMenuSeparator />
			<DropdownMenuGroup>
				<DropdownMenuItem onClick={() => showModal('CREATE_TICKET')}>
					<HeadsetIcon />
					{t('contact_support', {
						defaultValue: 'Contact support',
					})}
				</DropdownMenuItem>
				<DropdownMenuItem asChild>
					<Link to='/p/preferences'>
						<SettingsIcon />
						{t('menu.preferences', {
							defaultValue: 'Preferences',
						})}
					</Link>
				</DropdownMenuItem>
			</DropdownMenuGroup>
			<DropdownMenuSeparator />
			<DropdownMenuGroup>
				<DropdownMenuCheckboxItem
					checked={theme === 'dark'}
					onClick={() => {
						ampli.themeSwitchToDarkMode()
						setTheme('dark')
					}}
				>
					{t('app.theme_dark', {
						defaultValue: 'Dark theme',
					})}
				</DropdownMenuCheckboxItem>
				<DropdownMenuCheckboxItem
					checked={theme === 'light'}
					onClick={() => {
						ampli.themeSwitchToLightMode()
						setTheme('light')
					}}
				>
					{t('app.theme_base', {
						defaultValue: 'Light theme',
					})}
				</DropdownMenuCheckboxItem>
				<DropdownMenuCheckboxItem
					checked={theme === 'system'}
					onClick={() => {
						ampli.themeSwitchToSystemMode()
						setTheme('system')
					}}
				>
					{t('app.system', {
						defaultValue: 'System theme',
					})}
				</DropdownMenuCheckboxItem>
			</DropdownMenuGroup>
			<DropdownMenuSeparator />
			<DropdownMenuItem
				onClick={() => {
					logout()
					ampli.flush()
				}}
			>
				<LogOutIcon />
				{t('app.logout', {
					defaultValue: 'Logout',
				})}
			</DropdownMenuItem>
		</DropdownMenuContent>
	)
}

const SidebarTrigger = ({ onClick }: React.ComponentProps<typeof Button>) => {
	const { toggleSidebar, open } = useSidebar()
	const { t } = useTranslation()

	return (
		<SidebarMenuItem>
			<SidebarMenuButton
				className='cursor-pointer'
				size='sm'
				onClick={(event) => {
					onClick?.(event)
					toggleSidebar()
				}}
			>
				{!open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
				<span>
					{t('app.close_sidebar', {
						defaultValue: 'Close sidebar',
					})}
				</span>
			</SidebarMenuButton>
		</SidebarMenuItem>
	)
}
